<template>
  <div class="space-y-4">
    <p class="text-2xl font-medium">
      {{ t('sellCar') }}
    </p>

    <p>
      {{ t('reallyFleetOutCar') }}
    </p>

    <ThresholdDatePicker
      :threshold-date="thresholdDate"
      :initial-date="selectedDate"
      threshold-mode="min"
      :label="t('fleetOutDate')"
      @date-change="(date) => (selectedDate = date)"
    />

    <Divider />

    <div class="flex justify-end gap-4">
      <CVButton
        variant="danger"
        :disabled="!selectedDate"
        :is-loading="isUpdatingCar"
        @click.prevent="submit"
      >
        {{ t('yesFleetOutCar') }}
      </CVButton>
      <CVButton variant="ghost" :is-loading="isUpdatingCar" @click.prevent="emit('complete')">
        {{ t('cancel') }}
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Car } from '@/entities/cars/car.entity';
import { useI18n } from 'vue-i18n';
import { computed, ref } from 'vue';
import Divider from '@/components/Divider.vue';
import { useUpdateCar } from '@/queries/use-cars';
import { CarStatus } from '@/entities/cars/car-status.enum';
import ThresholdDatePicker from '@/components/booking-forms/ThresholdDatePicker.vue';
import CVButton from '@/components/buttons/CVButton.vue';

const props = defineProps<{
  car: Car;
}>();

const emit = defineEmits<{
  (e: 'complete'): void;
}>();

const { t } = useI18n();

const { mutateAsync: updateCar, isPending: isUpdatingCar } = useUpdateCar();

const selectedDate = ref<string | null>(props.car.availableUntil ?? null);

const thresholdDate = computed(() => {
  return props.car.availableUntil ?? new Date(8.64e15).toISOString();
});

const submit = async () => {
  if (!selectedDate.value) return;

  await updateCar({
    id: props.car.id,
    car: {
      status: CarStatus.FLOAT_OUT,
      availableUntil: selectedDate.value,
    },
  });
  emit('complete');
};
</script>

<i18n lang="json">
{
  "en": {
    "sellCar": "Sell Car",
    "confirm": "Confirm",
    "yesFleetOutCar": "Yes, Fleet Out Car",
    "fleetOutDate": "Fleet Out Date",
    "reallyFleetOutCar": "Do you really want to fleet out this car? This means, that the car is no longer available for bookings."
  },
  "de": {
    "sellCar": "Auto verkaufen",
    "confirm": "Bestätigen",
    "yesFleetOutCar": "Ja, Auto ausflotten",
    "fleetOutDate": "Ausflottungsdatum",
    "reallyFleetOutCar": "Möchtest du dieses Auto wirklich ausflotten? Das bedeutet, dass das Auto nicht mehr für Buchungen zur Verfügung steht."
  },
  "es": {
    "sellCar": "Vender vehículo",
    "confirm": "Confirmar",
    "yesFleetOutCar": "Sí, dar de baja el vehículo",
    "fleetOutDate": "Fecha de baja de flota",
    "reallyFleetOutCar": "¿Seguro que quieres dar de baja este vehículo? Dejará de estar disponible para reservas."
  }
}
</i18n>
